import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../services/api";
import PublicNavbar from "../components/public/PublicNavbar";
import PublicFooter from "../components/public/PublicFooter";
import PurchaseModal from "../components/public/PurchaseModal";
import ProductCard from "../components/product/ProductCard";
import {
    FaWhatsapp, FaShieldAlt, FaTruck, FaHeadset,
    FaStar, FaMinus, FaPlus, FaPlay,
    FaDraftingCompass, FaCheckCircle,
    FaChevronLeft, FaChevronRight, FaRulerCombined
} from "react-icons/fa";
import { buildWhatsAppUrl } from "../utils/whatsapp";
import { DEFAULT_LUMIGO_PRODUCTS } from "../data/defaultProducts";

const ProductDetails = () => {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const [activeTab, setActiveTab] = useState("Overview");
    const [showInquiryModal, setShowInquiryModal] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        setSelectedImageIndex(0);
        setQuantity(1);
    }, [id]);

    useEffect(() => {
        const fetchProductAndRelated = async () => {
            try {
                const { data } = await api.get(`/api/products/${id}`);
                setProduct(data);

                // Fetch all products to populate "Related Products"
                const allRes = await api.get("/api/products");
                const all = Array.isArray(allRes.data)
                    ? allRes.data
                    : allRes.data.products || DEFAULT_LUMIGO_PRODUCTS;
                const related = all.filter(p => p._id !== id && (p.id !== id)).slice(0, 4);
                setRelatedProducts(related);
            } catch (error) {
                console.warn("Product fetch error, attempting fallback:", error);
                const fallback = DEFAULT_LUMIGO_PRODUCTS.find(
                    p => p._id === id || p.modelNumber?.toLowerCase() === id?.toLowerCase() || p.sku?.toLowerCase() === id?.toLowerCase()
                ) || DEFAULT_LUMIGO_PRODUCTS[0];
                setProduct(fallback);
                setRelatedProducts(DEFAULT_LUMIGO_PRODUCTS.filter(p => p._id !== fallback._id).slice(0, 4));
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchProductAndRelated();
        }
    }, [id]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FFFFFF] text-[#263746]">
                <PublicNavbar />
                <div className="flex items-center justify-center py-40">
                    <div className="text-center">
                        <div className="w-10 h-10 border-2 border-[#E3B629] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-[#687482] text-xs font-semibold uppercase tracking-widest">
                            Loading Product Specifications...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen bg-[#FFFFFF] text-[#263746]">
                <PublicNavbar />
                <div className="max-w-xl mx-auto py-32 px-6 text-center">
                    <h2 className="text-2xl font-bold mb-3 text-[#263746]">Product Not Found</h2>
                    <p className="text-[#687482] text-sm mb-6">
                        The requested gobo projector model may have been moved or updated in our catalogue.
                    </p>
                    <Link
                        to="/products"
                        className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-bold px-6 py-2.5 rounded-lg text-xs uppercase tracking-wider inline-block transition"
                    >
                        Return to Catalogue
                    </Link>
                </div>
            </div>
        );
    }

    const specs = product.specifications || {};
    const images = (product.images && product.images.length > 0)
        ? product.images
        : [product.image || "/assets/lumigo/projector-1.jpg"];
    const modelNumber = product.modelNumber || product.sku || "DXAX-150T";
    const power = specs.ratedPower || "150W";
    const throwDistance = specs.projectionDistance || "3–25m";
    const ipRating = specs.protectionRating || "IP65";

    const standardPrice = product.pricing?.mrp || product.price || 38000;
    const discountedPrice = product.pricing?.finalPrice || product.discountPrice || product.price || 32500;
    const hasDiscount = product.pricing?.isOnOffer || (standardPrice > discountedPrice && discountedPrice > 0);
    const discountPercent = hasDiscount && standardPrice > 0
        ? Math.round(((standardPrice - discountedPrice) / standardPrice) * 100)
        : (product.pricing?.discountPercent || 14);

    const handleWhatsAppClick = () => {
        const msg = `Hello LUMIGO,\n\nI would like to request a commercial quote for:\nModel: ${modelNumber} (${product.name})\nQuantity: ${quantity} unit(s)\nCategory: ${product.category || "Commercial Projector"}\nPower: ${power}\nThrow Distance: ${throwDistance}\nProtection: ${ipRating}\n\nPlease share availability, lead time, and quotation details.`;
        window.open(buildWhatsAppUrl(msg), "_blank");
    };

    const tabs = ["Overview", "Specifications", "Applications", "Custom Gobo", "Gallery", "Reviews"];

    return (
        <div className="min-h-screen bg-[#FFFFFF] text-[#263746] font-sans pt-14">
            <PublicNavbar />

            {/* Breadcrumb Navigation matching Image 1 Panel 3 */}
            <div className="bg-[#F7F5EF] border-b border-[#E5E7EB] py-3 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto flex items-center text-xs text-[#687482] gap-2">
                    <Link to="/" className="hover:text-[#263746] transition">Home</Link>
                    <span>&gt;</span>
                    <Link to="/products" className="hover:text-[#263746] transition">Products</Link>
                    <span>&gt;</span>
                    <Link to={`/products?category=${encodeURIComponent(product.category || "Outdoor Projectors")}`} className="hover:text-[#263746] transition">
                        {product.category || "Outdoor Projectors"}
                    </Link>
                    <span>&gt;</span>
                    <span className="text-[#263746] font-semibold">{modelNumber}</span>
                </div>
            </div>

            {/* Main Product Showcase Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">

                    {/* LEFT COLUMN: Gallery with Vertical Thumbnails & Primary Image */}
                    <div className="lg:col-span-7 flex flex-col-reverse sm:flex-row gap-4">
                        {/* Thumbnail column */}
                        <div className="flex sm:flex-col gap-3 overflow-x-auto sm:overflow-y-auto sm:max-h-[500px] flex-shrink-0">
                            {images.map((img, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setSelectedImageIndex(idx)}
                                    className={`w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-all p-1 bg-[#FFFFFF] flex-shrink-0 flex items-center justify-center ${selectedImageIndex === idx
                                        ? "border-[#E3B629] shadow-sm"
                                        : "border-[#E5E7EB] hover:border-gray-400 opacity-75"
                                        }`}
                                >
                                    <img
                                        src={img}
                                        alt={`Thumbnail ${idx + 1}`}
                                        className="w-full h-full object-contain"
                                        onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                                    />
                                </button>
                            ))}
                        </div>

                        {/* Large Primary Image Container */}
                        <div className="flex-1 bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl relative p-6 sm:p-10 flex items-center justify-center min-h-[360px] sm:min-h-[480px] shadow-sm group">
                            {/* Best Seller Pill Badge */}
                            <div className="absolute top-4 right-4">
                                <span className="bg-[#E3B629] text-[#263746] text-[11px] font-black uppercase tracking-wider px-3 py-1 rounded shadow-sm">
                                    BEST SELLER
                                </span>
                            </div>

                            {/* Main equipment photo */}
                            <img
                                src={images[selectedImageIndex]}
                                alt={product.name}
                                className="max-w-full max-h-[380px] sm:max-h-[420px] object-contain transition-transform duration-300 group-hover:scale-105"
                                onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                            />

                            {/* Image Navigation Arrows */}
                            {images.length > 1 && (
                                <>
                                    <button
                                        onClick={() => setSelectedImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1))}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#FFFFFF]/90 hover:bg-[#FFFFFF] p-2.5 rounded-full text-[#263746] transition border border-[#E5E7EB] shadow-md"
                                        aria-label="Previous image"
                                    >
                                        <FaChevronLeft className="text-xs" />
                                    </button>
                                    <button
                                        onClick={() => setSelectedImageIndex(prev => (prev + 1) % images.length)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-[#FFFFFF]/90 hover:bg-[#FFFFFF] p-2.5 rounded-full text-[#263746] transition border border-[#E5E7EB] shadow-md"
                                        aria-label="Next image"
                                    >
                                        <FaChevronRight className="text-xs" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* RIGHT COLUMN: Product Information & B2B Actions */}
                    <div className="lg:col-span-5 space-y-5">
                        {/* Title & Model */}
                        <div>
                            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#263746] tracking-tight">
                                {modelNumber}
                            </h1>
                            <p className="text-base font-semibold text-[#687482] mt-1">
                                {product.name.replace(modelNumber, "").trim() || "Outdoor Gobo Projector (150W)"}
                            </p>
                        </div>

                        {/* Ratings */}
                        <div className="flex items-center gap-2">
                            <div className="flex text-[#E3B629] text-sm">
                                {[...Array(5)].map((_, i) => (
                                    <FaStar key={i} />
                                ))}
                            </div>
                            <span className="text-xs font-bold text-[#263746]">4.8</span>
                            <span className="text-xs text-[#687482]">(24 reviews)</span>
                        </div>

                        {/* Price Block */}
                        <div className="pt-2 border-t border-[#E5E7EB]">
                            <div className="flex items-baseline gap-3">
                                <span className="text-3xl font-extrabold text-[#263746]">
                                    ₹{discountedPrice.toLocaleString()}
                                </span>
                                {hasDiscount && (
                                    <>
                                        <span className="text-base text-[#9CA3AF] line-through font-medium">
                                            ₹{standardPrice.toLocaleString()}
                                        </span>
                                        <span className="bg-[#EF4444] text-white text-[11px] font-bold px-2 py-0.5 rounded">
                                            {discountPercent}% OFF
                                        </span>
                                    </>
                                )}
                            </div>
                            <p className="text-[12px] text-[#687482] mt-1">
                                Indicative commercial hardware price. Custom dichroic gobo glass quoted per artwork.
                            </p>
                        </div>

                        {/* Short Description */}
                        <p className="text-xs sm:text-sm text-[#687482] leading-relaxed">
                            {product.description ||
                                "High brightness outdoor gobo projector with IP65 protection, ideal for commercial branding, safety signage, decorative projection and industrial applications."}
                        </p>

                        {/* Quantity Selector & Enquire Now Row */}
                        <div className="flex items-center gap-4 pt-2">
                            {/* Quantity Selector */}
                            <div className="flex items-center border border-[#E5E7EB] rounded-lg bg-[#F7F5EF] overflow-hidden">
                                <button
                                    onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                                    className="p-3 text-[#263746] hover:bg-[#E5E7EB] transition"
                                    aria-label="Decrease quantity"
                                >
                                    <FaMinus className="text-[10px]" />
                                </button>
                                <span className="px-4 text-sm font-bold text-[#263746] min-w-[36px] text-center">
                                    {quantity}
                                </span>
                                <button
                                    onClick={() => setQuantity(prev => prev + 1)}
                                    className="p-3 text-[#263746] hover:bg-[#E5E7EB] transition"
                                    aria-label="Increase quantity"
                                >
                                    <FaPlus className="text-[10px]" />
                                </button>
                            </div>

                            {/* Enquire Now Button */}
                            <button
                                onClick={() => setShowInquiryModal(true)}
                                className="flex-1 bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold py-3.5 px-6 rounded-lg text-xs uppercase tracking-wider transition shadow-sm text-center"
                            >
                                Enquire Now
                            </button>
                        </div>

                        {/* WhatsApp Action Button */}
                        <button
                            onClick={handleWhatsAppClick}
                            className="w-full bg-[#168B53] hover:bg-[#127244] text-white font-bold py-3.5 px-6 rounded-lg text-xs uppercase tracking-wider transition shadow-sm flex items-center justify-center gap-2.5"
                        >
                            <FaWhatsapp className="text-lg" /> Enquire on WhatsApp
                        </button>

                        {/* 4 Icon Trust Badges Grid */}
                        <div className="grid grid-cols-4 gap-2 pt-6 border-t border-[#E5E7EB] text-center">
                            <div className="flex flex-col items-center">
                                <div className="w-10 h-10 rounded-full bg-[#F7F5EF] flex items-center justify-center text-[#263746] mb-1.5 border border-[#E5E7EB]">
                                    <FaTruck className="text-sm text-[#E3B629]" />
                                </div>
                                <span className="text-[11px] font-semibold text-[#263746] leading-tight">PAN India Delivery</span>
                            </div>

                            <div className="flex flex-col items-center">
                                <div className="w-10 h-10 rounded-full bg-[#F7F5EF] flex items-center justify-center text-[#263746] mb-1.5 border border-[#E5E7EB]">
                                    <FaShieldAlt className="text-sm text-[#E3B629]" />
                                </div>
                                <span className="text-[11px] font-semibold text-[#263746] leading-tight">1 Year Warranty</span>
                            </div>

                            <div className="flex flex-col items-center">
                                <div className="w-10 h-10 rounded-full bg-[#F7F5EF] flex items-center justify-center text-[#263746] mb-1.5 border border-[#E5E7EB]">
                                    <FaHeadset className="text-sm text-[#E3B629]" />
                                </div>
                                <span className="text-[11px] font-semibold text-[#263746] leading-tight">Expert Support</span>
                            </div>

                            <div className="flex flex-col items-center">
                                <div className="w-10 h-10 rounded-full bg-[#F7F5EF] flex items-center justify-center text-[#263746] mb-1.5 border border-[#E5E7EB]">
                                    <FaDraftingCompass className="text-sm text-[#E3B629]" />
                                </div>
                                <span className="text-[11px] font-semibold text-[#263746] leading-tight">Custom Gobo Design</span>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Tabs Section matching Panel 3 */}
                <div className="mt-14 pt-8 border-t border-[#E5E7EB]">
                    {/* Tab Navigation Headers */}
                    <div className="flex items-center gap-8 border-b border-[#E5E7EB] overflow-x-auto pb-px">
                        {tabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`pb-3 text-sm font-bold tracking-wide transition-all border-b-2 whitespace-nowrap ${activeTab === tab
                                    ? "border-[#E3B629] text-[#263746]"
                                    : "border-transparent text-[#687482] hover:text-[#263746]"
                                    }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    {/* Tab Content Display */}
                    <div className="py-8">
                        {/* OVERVIEW / SPECIFICATIONS TAB */}
                        {(activeTab === "Overview" || activeTab === "Specifications") && (
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                                {/* Left Side: Overview text & Specifications Table */}
                                <div className="lg:col-span-8 space-y-8">
                                    <div>
                                        <h3 className="text-lg font-bold text-[#263746] mb-3">Product Overview</h3>
                                        <p className="text-xs sm:text-sm text-[#687482] leading-relaxed">
                                            The {modelNumber} is a high-performance outdoor gobo projector with OSRAM LED light source, delivering sharp and vibrant projections. It is engineered specifically for commercial branding, safety signage, decorative projection and demanding industrial applications. With active cooling and IP65 weatherproof casing, it withstands rain, extreme heat, and ambient factory particulate.
                                        </p>
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-bold text-[#263746]">Specifications</h3>
                                            <span className="text-[11px] font-bold text-[#168B53] flex items-center gap-1.5 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                                                <FaCheckCircle className="text-xs" /> Verified Commercial Specifications
                                            </span>
                                        </div>

                                        <div className="border border-[#E5E7EB] rounded-xl overflow-hidden text-xs">
                                            <table className="w-full text-left border-collapse">
                                                <tbody>
                                                    <tr className="border-b border-[#E5E7EB] bg-[#F7F5EF]">
                                                        <td className="py-3 px-4 font-bold text-[#263746] w-1/2">Model</td>
                                                        <td className="py-3 px-4 text-[#687482] font-mono">{modelNumber}</td>
                                                    </tr>
                                                    <tr className="border-b border-[#E5E7EB] bg-[#FFFFFF]">
                                                        <td className="py-3 px-4 font-bold text-[#263746]">Projection Distance</td>
                                                        <td className="py-3 px-4 text-[#687482]">{specs.projectionDistance || "3 – 25 Metres"}</td>
                                                    </tr>
                                                    <tr className="border-b border-[#E5E7EB] bg-[#F7F5EF]">
                                                        <td className="py-3 px-4 font-bold text-[#263746]">Voltage</td>
                                                        <td className="py-3 px-4 text-[#687482]">{specs.voltage || "90–220V 50/60Hz"}</td>
                                                    </tr>
                                                    <tr className="border-b border-[#E5E7EB] bg-[#FFFFFF]">
                                                        <td className="py-3 px-4 font-bold text-[#263746]">Light Source Brand</td>
                                                        <td className="py-3 px-4 text-[#687482]">{specs.lightSourceBrand || "OSRAM"}</td>
                                                    </tr>
                                                    <tr className="border-b border-[#E5E7EB] bg-[#F7F5EF]">
                                                        <td className="py-3 px-4 font-bold text-[#263746]">Power</td>
                                                        <td className="py-3 px-4 text-[#687482] font-semibold">{specs.ratedPower || "150W"}</td>
                                                    </tr>
                                                    <tr className="border-b border-[#E5E7EB] bg-[#FFFFFF]">
                                                        <td className="py-3 px-4 font-bold text-[#263746]">Waterproof Rating</td>
                                                        <td className="py-3 px-4 text-[#687482] font-semibold">{specs.protectionRating || "IP65"}</td>
                                                    </tr>
                                                    <tr className="border-b border-[#E5E7EB] bg-[#F7F5EF]">
                                                        <td className="py-3 px-4 font-bold text-[#263746]">Light Source Life</td>
                                                        <td className="py-3 px-4 text-[#687482]">{specs.lightSourceLifetime || "80,000 Hours"}</td>
                                                    </tr>
                                                    <tr className="border-b border-[#E5E7EB] bg-[#FFFFFF]">
                                                        <td className="py-3 px-4 font-bold text-[#263746]">Luminous Flux</td>
                                                        <td className="py-3 px-4 text-[#687482]">{specs.luminousFlux || "15000 LM"}</td>
                                                    </tr>
                                                    <tr className="border-b border-[#E5E7EB] bg-[#F7F5EF]">
                                                        <td className="py-3 px-4 font-bold text-[#263746]">Colour Temperature</td>
                                                        <td className="py-3 px-4 text-[#687482]">{specs.colourTemperature || "9500K"}</td>
                                                    </tr>
                                                    <tr className="border-b border-[#E5E7EB] bg-[#FFFFFF]">
                                                        <td className="py-3 px-4 font-bold text-[#263746]">Weight</td>
                                                        <td className="py-3 px-4 text-[#687482]">{specs.productWeight || "3.5 kg"}</td>
                                                    </tr>
                                                    <tr className="bg-[#F7F5EF]">
                                                        <td className="py-3 px-4 font-bold text-[#263746]">Packaging Size</td>
                                                        <td className="py-3 px-4 text-[#687482]">{specs.dimensions || "34 × 23 × 20 cm"}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Side: Key Highlights & Dimensional Drawing */}
                                <div className="lg:col-span-4 space-y-6">
                                    {/* Key Highlights Card */}
                                    <div className="bg-[#F7F5EF] border border-[#E5E7EB] rounded-2xl p-6">
                                        <h4 className="text-base font-bold text-[#263746] mb-4">Key Highlights</h4>
                                        <ul className="space-y-3 text-xs text-[#263746] font-medium">
                                            <li className="flex items-center gap-2.5">
                                                <span className="w-2 h-2 rounded-full bg-[#E3B629]"></span>
                                                High Brightness {specs.luminousFlux || "15000LM"}
                                            </li>
                                            <li className="flex items-center gap-2.5">
                                                <span className="w-2 h-2 rounded-full bg-[#E3B629]"></span>
                                                {specs.protectionRating || "IP65"} Waterproof
                                            </li>
                                            <li className="flex items-center gap-2.5">
                                                <span className="w-2 h-2 rounded-full bg-[#E3B629]"></span>
                                                {specs.projectionDistance || "3 – 25 Metres"} Projection
                                            </li>
                                            <li className="flex items-center gap-2.5">
                                                <span className="w-2 h-2 rounded-full bg-[#E3B629]"></span>
                                                Long Life {specs.lightSourceLifetime || "80,000 Hours"}
                                            </li>
                                            <li className="flex items-center gap-2.5">
                                                <span className="w-2 h-2 rounded-full bg-[#E3B629]"></span>
                                                Custom Logo Gobo Ready
                                            </li>
                                            <li className="flex items-center gap-2.5">
                                                <span className="w-2 h-2 rounded-full bg-[#E3B629]"></span>
                                                Static or Rotating Display
                                            </li>
                                        </ul>
                                    </div>

                                    {/* Dimensional Drawing Schematic Card */}
                                    <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-6 shadow-sm">
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="text-sm font-bold text-[#263746] flex items-center gap-2">
                                                <FaRulerCombined className="text-[#E3B629]" /> Dimensional Drawing
                                            </h4>
                                            <span className="text-[10px] text-[#687482] font-mono">Dimensions in mm</span>
                                        </div>

                                        {/* Stylized Technical Blueprint Diagram */}
                                        <div className="bg-[#F7F5EF] border border-dashed border-[#E5E7EB] rounded-xl p-4 flex flex-col items-center justify-center">
                                            <svg viewBox="0 0 300 180" className="w-full max-w-[260px] text-[#263746]">
                                                {/* Projector Body Outline */}
                                                <rect x="70" y="40" width="160" height="75" rx="6" fill="#FFFFFF" stroke="#263746" strokeWidth="2" />
                                                {/* Lens Housing */}
                                                <rect x="230" y="48" width="35" height="59" rx="3" fill="#E3B629" fillOpacity="0.15" stroke="#263746" strokeWidth="2" />
                                                <ellipse cx="265" cy="77" rx="6" ry="29" fill="#FFFFFF" stroke="#263746" strokeWidth="2" />
                                                {/* Heat Sink Fins */}
                                                <line x1="90" y1="40" x2="90" y2="115" stroke="#E5E7EB" strokeWidth="2" />
                                                <line x1="110" y1="40" x2="110" y2="115" stroke="#E5E7EB" strokeWidth="2" />
                                                <line x1="130" y1="40" x2="130" y2="115" stroke="#E5E7EB" strokeWidth="2" />
                                                <line x1="150" y1="40" x2="150" y2="115" stroke="#E5E7EB" strokeWidth="2" />
                                                <line x1="170" y1="40" x2="170" y2="115" stroke="#E5E7EB" strokeWidth="2" />
                                                {/* Bracket Arm and Base Mount */}
                                                <path d="M120 115 L120 150 L180 150 L180 115" fill="none" stroke="#263746" strokeWidth="2.5" />
                                                <rect x="105" y="150" width="90" height="12" rx="3" fill="#263746" />
                                                {/* Dimension Labels */}
                                                <text x="145" y="25" textAnchor="middle" fill="#687482" fontSize="10" fontFamily="sans-serif">235 mm</text>
                                                <line x1="70" y1="30" x2="265" y2="30" stroke="#687482" strokeWidth="1" strokeDasharray="3 3" />
                                                <text x="35" y="80" textAnchor="middle" fill="#687482" fontSize="10" fontFamily="sans-serif">147 mm</text>
                                                <line x1="55" y1="40" x2="55" y2="115" stroke="#687482" strokeWidth="1" strokeDasharray="3 3" />
                                            </svg>
                                            <div className="grid grid-cols-2 gap-2 w-full mt-3 text-[11px] text-[#687482] text-center">
                                                <div>Length: <strong className="text-[#263746]">235 mm</strong></div>
                                                <div>Height: <strong className="text-[#263746]">147 mm</strong></div>
                                                <div>Lens Ø: <strong className="text-[#263746]">60 mm</strong></div>
                                                <div>Base Width: <strong className="text-[#263746]">140 mm</strong></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* APPLICATIONS TAB */}
                        {activeTab === "Applications" && (
                            <div className="space-y-6">
                                <h3 className="text-lg font-bold text-[#263746]">Recommended Applications</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    <div className="bg-[#F7F5EF] p-5 rounded-xl border border-[#E5E7EB]">
                                        <h4 className="font-bold text-[#263746] mb-2">Industrial Safety Signage</h4>
                                        <p className="text-xs text-[#687482] leading-relaxed">
                                            Projects vibrant STOP, Forklift Crossing, and Overhead Crane warnings onto warehouse floors where painted lines quickly wear off.
                                        </p>
                                    </div>
                                    <div className="bg-[#F7F5EF] p-5 rounded-xl border border-[#E5E7EB]">
                                        <h4 className="font-bold text-[#263746] mb-2">Commercial Building Facades</h4>
                                        <p className="text-xs text-[#687482] leading-relaxed">
                                            Illuminates corporate logos, brand emblems, and promotional designs onto multi-storey exterior walls with crystal-clear focus up to 25m.
                                        </p>
                                    </div>
                                    <div className="bg-[#F7F5EF] p-5 rounded-xl border border-[#E5E7EB]">
                                        <h4 className="font-bold text-[#263746] mb-2">Hotels, Showrooms & Retail</h4>
                                        <p className="text-xs text-[#687482] leading-relaxed">
                                            Creates premium entrance welcomes, monogram displays, and product spotlight accents that captivate arriving guests.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* CUSTOM GOBO TAB */}
                        {activeTab === "Custom Gobo" && (
                            <div className="bg-[#F7F5EF] border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 space-y-4">
                                <h3 className="text-lg font-bold text-[#263746]">Custom Dichroic Glass Gobo Manufacturing</h3>
                                <p className="text-xs sm:text-sm text-[#687482] leading-relaxed">
                                    Every LUMIGO projector supports high-precision laser-etched dichroic glass gobos crafted specifically to your brand artwork, corporate typography, or festive motif.
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-3 text-xs">
                                    <div className="bg-[#FFFFFF] p-4 rounded-xl border border-[#E5E7EB]">
                                        <span className="font-bold text-[#263746] block mb-1">Single Colour Monochrome</span>
                                        <p className="text-[#687482]">Ideal for crisp white or single-tint company logos with maximum optical transmission.</p>
                                    </div>
                                    <div className="bg-[#FFFFFF] p-4 rounded-xl border border-[#E5E7EB]">
                                        <span className="font-bold text-[#263746] block mb-1">Dual / Triple Colour</span>
                                        <p className="text-[#687482]">Precision coated dichroic layers for multi-colour corporate branding without colour fading.</p>
                                    </div>
                                    <div className="bg-[#FFFFFF] p-4 rounded-xl border border-[#E5E7EB]">
                                        <span className="font-bold text-[#263746] block mb-1">Full Colour Photographic</span>
                                        <p className="text-[#687482]">Complex gradients, realistic art, and rangoli patterns engineered to withstand high thermal loads.</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* GALLERY / VIDEO TAB */}
                        {activeTab === "Gallery" && (
                            <div className="space-y-6">
                                <h3 className="text-lg font-bold text-[#263746]">Projection Demonstration Gallery</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {images.map((img, idx) => (
                                        <div key={idx} className="bg-[#F7F5EF] border border-[#E5E7EB] rounded-xl overflow-hidden aspect-video flex items-center justify-center p-3">
                                            <img src={img} alt={`Demonstration ${idx + 1}`} className="w-full h-full object-contain" />
                                        </div>
                                    ))}
                                </div>
                                <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-6">
                                    <h4 className="text-sm font-bold text-[#263746] mb-3 flex items-center gap-2">
                                        <FaPlay className="text-[#E3B629] text-xs" /> Dynamic Rotation Video
                                    </h4>
                                    <div className="max-w-2xl aspect-video rounded-xl overflow-hidden bg-black">
                                        <video controls poster={images[0]} className="w-full h-full object-cover">
                                            <source src={product.video || product.videoUrl || "/assets/lumigo/video-demo-1.mp4"} type="video/mp4" />
                                            Your browser does not support HTML video playback.
                                        </video>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* REVIEWS TAB */}
                        {activeTab === "Reviews" && (
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-bold text-[#263746]">Client Reviews & Field Feedback</h3>
                                    <span className="text-xs font-bold text-[#E3B629]">★ 4.8 / 5.0 Average</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-[#F7F5EF] border border-[#E5E7EB] p-5 rounded-xl">
                                        <div className="flex text-[#E3B629] text-xs mb-2">★★★★★</div>
                                        <p className="text-xs text-[#263746] font-medium mb-2">"Installed for warehouse walkway warning. Projection is vivid even under bright industrial high-bay lighting."</p>
                                        <span className="text-[11px] text-[#687482] block">— Plant Operations Manager, Pune Industrial Area</span>
                                    </div>
                                    <div className="bg-[#F7F5EF] border border-[#E5E7EB] p-5 rounded-xl">
                                        <div className="flex text-[#E3B629] text-xs mb-2">★★★★★</div>
                                        <p className="text-xs text-[#263746] font-medium mb-2">"Custom gobo glass resolution was beyond our expectations. Sharp hotel branding projected across our courtyard."</p>
                                        <span className="text-[11px] text-[#687482] block">— Hospitality Facilities Director, Mumbai</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Related Products Section matching Panel 3 */}
                {relatedProducts.length > 0 && (
                    <div className="mt-16 pt-10 border-t border-[#E5E7EB]">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-2xl font-extrabold text-[#263746] tracking-tight">
                                Related Products
                            </h2>
                            <Link
                                to="/products"
                                className="text-xs font-bold text-[#E3B629] hover:text-[#cca021] transition flex items-center gap-1"
                            >
                                View All &rarr;
                            </Link>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {relatedProducts.map((p) => (
                                <ProductCard key={p._id || p.id} product={p} />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <PublicFooter />

            {/* Direct Purchase & Instant Client Registration Modal */}
            {showInquiryModal && (
                <PurchaseModal
                    product={product}
                    initialQuantity={quantity}
                    onClose={() => setShowInquiryModal(false)}
                />
            )}
        </div>
    );
};

export default ProductDetails;
